import { useEffect, useMemo, useState } from "react";

import Spinner from "components/Spinner";
import { useAppStore } from "lib/store/useAppStore";
import {
  getRoleAdminAccounts,
  updateRoleAdminAccountPermission,
} from "lib/api/roleAdmin";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";
import { PERM } from "lib/utils/permission/permissions";
import { formatSystemRole } from "lib/utils/roleLabels";

const EDITABLE_ROLES = [
  {
    value: "user",
    label: formatSystemRole("user"),
  },
  {
    value: "manager",
    label: formatSystemRole("manager"),
  },
  {
    value: "admin",
    label: formatSystemRole("admin"),
  },
];

const ROLE_RANK = { user: 1, manager: 2, admin: 3, master: 4 };

const EDITABLE_PERMISSIONS = [
  {
    key: PERM.PROJECT_READ,
    label: "프로젝트 조회",
    description: "자신이 생성한 프로젝트를 조회할 수 있는 권한입니다.",
  },
  {
    key: PERM.PROJECT_CREATE_FILE_UPLOAD,
    label: "프로젝트 생성 / 파일 업로드",
    description:
      "파일 업로드를 위한 프로젝트를 생성하고, 해당 프로젝트에 파일을 업로드할 수 있는 권한입니다.",
  },
  {
    key: PERM.PROJECT_UPDATE_FILE_UPDATE,
    label: "프로젝트 / 파일 수정",
    description: "등록된 프로젝트 및 파일의 정보를 수정할 수 있는 권한입니다.",
  },
  {
    key: PERM.PROJECT_DELETE_FILE_DELETE,
    label: "프로젝트 / 파일 삭제",
    description: "등록된 프로젝트 및 파일을 삭제할 수 있는 권한입니다.",
  },
  {
    key: PERM.REPORT_CREATE,
    label: "보고서 생성",
    description:
      "PDF, Excel 형식으로 파일 또는 프로젝트 단위의 보고서를 생성할 수 있는 권한입니다.",
  },
  {
    key: PERM.ORGANIZATION_CREATE,
    label: "조직 생성",
    description: "새 조직을 생성할 수 있는 권한입니다.",
  },
  {
    key: PERM.OBJECT3D_CREATE,
    label: "3D 데이터 생성",
    description: "동영상 파일을 사용하여 3D 분석 데이터를 생성할 수 있는 권한입니다.",
  },
  {
    key: PERM.USER_REGISTER,
    label: "사용자 등록",
    description:
      "해당 계정의 이메일을 가입 승인 이메일로 등록하고, 해당 이메일을 통해 다른 사용자의 회원가입 요청을 승인 또는 거절할 수 있는 권한입니다.",
  },
  {
    key: PERM.USER_PERMISSION_EDIT,
    label: "사용자 권한 변경",
    description: "자신이 관리하는 계정들의 권한을 변경할 수 있는 권한입니다.",
  },
];

const ADMIN_ROLE_ONLY_PERMISSION_KEYS = new Set([
  PERM.USER_REGISTER,
  PERM.USER_PERMISSION_EDIT,
]);

const ROLE_ADMIN_PERMISSION_ROLES = new Set(["admin", "manager"]);

const ENFORCED_PERMISSION_KEYS = [
  PERM.PROJECT_READ,
  PERM.NOTICE_WRITE_EDIT,
];

function formatDate(value) {
  return formatKoreanDateTime(value, { year: "numeric" });
}

function normalizePermission(permission) {
  return permission && typeof permission === "object" ? permission : {};
}

function normalizeRole(role) {
  return role === "master" ||
    role === "admin" ||
    role === "manager" ||
    role === "user"
    ? role
    : "user";
}

function getEditableRolesByActor(actorRole) {
  const actorRank = ROLE_RANK[normalizeRole(actorRole)] ?? ROLE_RANK.user;
  return EDITABLE_ROLES.filter(
    (role) => (ROLE_RANK[role.value] ?? ROLE_RANK.user) <= actorRank,
  );
}

function isPermissionDisabledForRole(key, role) {
  return (
    key === PERM.PROJECT_READ ||
    (ADMIN_ROLE_ONLY_PERMISSION_KEYS.has(key) &&
      !ROLE_ADMIN_PERMISSION_ROLES.has(normalizeRole(role)))
  );
}

function normalizePermissionForRole(permission, role) {
  const next = { ...normalizePermission(permission) };
  next[PERM.PROJECT_READ] = true;
  next[PERM.NOTICE_WRITE_EDIT] = false;

  if (!ROLE_ADMIN_PERMISSION_ROLES.has(normalizeRole(role))) {
    for (const key of ADMIN_ROLE_ONLY_PERMISSION_KEYS) {
      next[key] = false;
    }
  }

  return next;
}

function hasPermissionChanges(original, draft, role) {
  const normalizedDraft = normalizePermissionForRole(draft, role);
  const keys = new Set([
    ...ENFORCED_PERMISSION_KEYS,
    ...EDITABLE_PERMISSIONS.map(({ key }) => key),
  ]);

  for (const key of keys) {
    if (Boolean(original?.[key]) !== Boolean(normalizedDraft?.[key])) {
      return true;
    }
  }

  return false;
}

export default function AdminOperatorPermissionsPage() {
  const showAlert = useAppStore((s) => s.showAlert);
  const actorRole = useAppStore((s) => s.user?.role ?? "user");
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [draftRole, setDraftRole] = useState("user");
  const [draftPermission, setDraftPermission] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedId) ?? null,
    [accounts, selectedId],
  );

  const filteredAccounts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return accounts;

    return accounts.filter((account) => {
      return (
        (account.user_name ?? "").toLowerCase().includes(keyword) ||
        (account.email ?? "").toLowerCase().includes(keyword) ||
        (account.company_name ?? "").toLowerCase().includes(keyword) ||
        (account.organization_name ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [accounts, search]);

  const editableRoles = useMemo(
    () => getEditableRolesByActor(actorRole),
    [actorRole],
  );

  const roleOptions = useMemo(() => {
    const currentRole = normalizeRole(selectedAccount?.role);
    const options = editableRoles.map((role) => ({
      ...role,
      disabled: false,
    }));

    if (
      selectedAccount &&
      !options.some((role) => role.value === currentRole)
    ) {
      options.unshift({
        value: currentRole,
        label: formatSystemRole(currentRole),
        disabled: true,
      });
    }

    return options;
  }, [editableRoles, selectedAccount]);

  const canEditSelectedAccount = useMemo(() => {
    if (!selectedAccount) return false;

    const actorRank = ROLE_RANK[normalizeRole(actorRole)] ?? ROLE_RANK.user;
    const selectedRank =
      ROLE_RANK[normalizeRole(selectedAccount.role)] ?? ROLE_RANK.user;
    return selectedRank <= actorRank;
  }, [actorRole, selectedAccount]);

  const loadAccounts = async () => {
    setIsLoading(true);
    const result = await getRoleAdminAccounts();
    setIsLoading(false);

    if (!result.success) {
      setAccounts([]);
      showAlert({
        text: result.message || "생성 계정 목록을 불러오지 못했습니다.",
        success: false,
        autoClose: true,
      });
      return;
    }

    const nextAccounts = result.data?.accounts ?? [];
    setAccounts(nextAccounts);
    setSelectedId((current) =>
      nextAccounts.some((account) => account.id === current)
        ? current
        : nextAccounts[0]?.id ?? null,
    );
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextRole = normalizeRole(selectedAccount?.role);
    setDraftRole(nextRole);
    setDraftPermission(
      normalizePermissionForRole(selectedAccount?.permission, nextRole),
    );
  }, [selectedAccount]);

  const originalRole = normalizeRole(selectedAccount?.role);
  const originalPermission = normalizePermission(selectedAccount?.permission);
  const hasRoleChanges = originalRole !== draftRole;
  const hasChanges =
    hasRoleChanges ||
    hasPermissionChanges(originalPermission, draftPermission, draftRole);

  const handleTogglePermission = (key) => {
    if (!canEditSelectedAccount) return;
    if (isPermissionDisabledForRole(key, draftRole)) return;

    setDraftPermission((current) => ({
      ...current,
      [key]: !Boolean(current?.[key]),
    }));
  };

  const handleChangeRole = (role) => {
    if (!canEditSelectedAccount) return;
    if (!editableRoles.some((option) => option.value === role)) return;

    setDraftRole(role);
    setDraftPermission((current) => normalizePermissionForRole(current, role));
  };

  const handleSave = async () => {
    if (!selectedAccount || !hasChanges || isSaving || !canEditSelectedAccount) {
      return;
    }

    setIsSaving(true);
    const result = await updateRoleAdminAccountPermission(selectedAccount.id, {
      role: draftRole,
      permission: normalizePermissionForRole(draftPermission, draftRole),
    });
    setIsSaving(false);

    showAlert({
      text: result.message || "계정 권한을 저장했습니다.",
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      const nextPermission = result.data?.permission ?? draftPermission;
      const nextRole = result.data?.role ?? draftRole;
      setAccounts((current) =>
        current.map((account) =>
          account.id === selectedAccount.id
            ? { ...account, role: nextRole, permission: nextPermission }
            : account,
        ),
      );
    }
  };

  return (
    <section className="content admin roleAdminPage">
      <div className="roleAdminHeader">
        <div>
          <h2>권한 설정</h2>
          <p>내가 승인해서 생성된 계정의 시스템 권한과 기능 권한만 설정합니다.</p>
        </div>
      </div>

      {isLoading ? <Spinner /> : null}

      <div className="roleAdminSplit">
        <section className="roleAdminPanel">
          <div className="roleAdminPanel__header">
            <h3>생성 계정</h3>
            <span>{filteredAccounts.length}명</span>
          </div>
          <input
            className="roleAdminSearch"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="이름, 이메일, 회사, 조직 검색"
            type="search"
            value={search}
          />
          <div className="roleAdminAccountList">
            {filteredAccounts.map((account) => (
              <button
                className={`roleAdminAccountItem ${
                  selectedId === account.id ? "isSelected" : ""
                }`}
                key={account.id}
                onClick={() => setSelectedId(account.id)}
                type="button"
              >
                <div>
                  <strong>{account.user_name || "-"}</strong>
                  <span>{account.email}</span>
                </div>
                <small>{formatSystemRole(account.role || "user")}</small>
              </button>
            ))}
            {filteredAccounts.length === 0 ? (
              <div className="roleAdminEmpty">계정이 없습니다.</div>
            ) : null}
          </div>
        </section>

        <section className="roleAdminPanel">
          <div className="roleAdminPanel__header">
            <h3>권한 상세</h3>
            {selectedAccount ? (
              <span>{formatSystemRole(selectedAccount.role)}</span>
            ) : null}
          </div>

          {!selectedAccount ? (
            <div className="roleAdminEmpty">계정을 선택해 주세요.</div>
          ) : (
            <>
              <dl className="roleAdminDetailList">
                <div>
                  <dt>계정</dt>
                  <dd>
                    {selectedAccount.user_name || "-"} / {selectedAccount.email}
                  </dd>
                </div>
                <div>
                  <dt>조직</dt>
                  <dd>{selectedAccount.organization_name || "-"}</dd>
                </div>
                <div>
                  <dt>최근 로그인</dt>
                  <dd>{formatDate(selectedAccount.last_login)}</dd>
                </div>
                <div>
                  <dt>최근 활동</dt>
                  <dd>{formatDate(selectedAccount.latest_activity_at)}</dd>
                </div>
              </dl>

              <div className="roleAdminRoleControl">
                <span>시스템 권한</span>
                <div>
                  {roleOptions.map((role) => (
                    <label key={role.value}>
                      <input
                        checked={draftRole === role.value}
                        disabled={role.disabled || !canEditSelectedAccount}
                        name="roleAdminSystemRole"
                        onChange={() => handleChangeRole(role.value)}
                        type="radio"
                      />
                      <span>{role.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="roleAdminPermissionList">
                {EDITABLE_PERMISSIONS.map((permission) => {
                  const isDisabled = isPermissionDisabledForRole(
                    permission.key,
                    draftRole,
                  );

                  return (
                    <label
                      className={`roleAdminPermissionItem ${
                        isDisabled ? "isDisabled" : ""
                      }`}
                      key={permission.key}
                    >
                      <input
                        checked={Boolean(draftPermission?.[permission.key])}
                        disabled={isDisabled || !canEditSelectedAccount}
                        onChange={() => handleTogglePermission(permission.key)}
                        type="checkbox"
                      />
                      <span className="roleAdminPermissionItem__label">
                        {permission.label}
                      </span>
                      <span className="roleAdminPermissionItem__description">
                        {permission.description}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="roleAdminActionRow">
                <button
                  className="roleAdminPrimaryButton"
                  disabled={!hasChanges || isSaving || !canEditSelectedAccount}
                  onClick={handleSave}
                  type="button"
                >
                  저장
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </section>
  );
}
